import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { downloadCsv } from '@qtek/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class AiChatActionHandlerService {
  private readonly router = inject(Router);

  onMessageContentClick(event: MouseEvent): void {
    if (this.shouldIgnoreClick(event)) {
      return;
    }

    const target = event.target as Element | null;
    if (!target) {
      return;
    }

    if (this.tryHandleExportClick(target, event)) {
      return;
    }

    if (!this.canHandleNavigationClick(event)) {
      return;
    }

    this.tryHandleActionLinkNavigation(target, event);
  }

  private shouldIgnoreClick(event: MouseEvent): boolean {
    return event.defaultPrevented;
  }

  private tryHandleExportClick(target: Element, event: MouseEvent): boolean {
    const exportButton = target.closest(
      'button.allo-export, button.qt-table-export-btn'
    ) as HTMLButtonElement | null;
    if (!exportButton) {
      return false;
    }

    event.preventDefault();
    this.exportTableAsCsv(exportButton);
    return true;
  }

  private canHandleNavigationClick(event: MouseEvent): boolean {
    return !(
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  private tryHandleActionLinkNavigation(
    target: Element,
    event: MouseEvent
  ): boolean {
    const actionLink = target.closest(
      'a.qt-action-link'
    ) as HTMLAnchorElement | null;
    const targetUrl = this.getInternalActionLinkTarget(actionLink);

    if (!targetUrl) {
      return false;
    }

    event.preventDefault();
    void this.navigateToUrl(targetUrl);
    return true;
  }

  private getInternalActionLinkTarget(
    actionLink: HTMLAnchorElement | null
  ): string | null {
    if (!actionLink) {
      return null;
    }

    const href = actionLink.getAttribute('href')?.trim();
    if (!href || /^(?:https?:)?\/\//i.test(href)) {
      return null;
    }

    return href.startsWith('/') ? href : `/${href}`;
  }

  /*
   * Navigate to a target URL, handling both main app and popout window contexts.
   * In a popout window, delegates navigation to the opener via postMessage.
   */
  async navigateToUrl(targetUrl: string): Promise<void> {
    if (this.isPopoutWindow()) {
      this.navigateOpenerWindow(targetUrl);
      return;
    }

    await this.navigateWithRouteReinit(targetUrl);
  }

  private async navigateWithRouteReinit(targetUrl: string): Promise<void> {
    const currentPath = this.stripQueryAndHash(this.router.url);
    const targetPath = this.stripQueryAndHash(targetUrl);

    if (!this.shouldReinitializeDealRoute(currentPath, targetPath)) {
      await this.router.navigateByUrl(targetUrl);
      return;
    }

    const resetUrl = this.getDealRouteParent(targetPath);
    if (!resetUrl) {
      await this.router.navigateByUrl(targetUrl);
      return;
    }

    await this.router.navigateByUrl(resetUrl, { skipLocationChange: true });
    await this.router.navigateByUrl(targetUrl);
  }

  private shouldReinitializeDealRoute(
    currentPath: string,
    targetPath: string
  ): boolean {
    if (!currentPath || currentPath === targetPath) {
      return false;
    }

    return (
      !!this.getDealRouteParent(currentPath) &&
      !!this.getDealRouteParent(targetPath)
    );
  }

  private getDealRouteParent(url: string): string | null {
    const match = url.match(/^(.*)\/deal\/[^/?#]+$/);
    if (!match) {
      return null;
    }

    return match[1] || '/';
  }

  private stripQueryAndHash(url: string): string {
    return url.split(/[?#]/, 1)[0] ?? url;
  }

  private isPopoutWindow(): boolean {
    try {
      return !!window.opener && !window.opener.closed;
    } catch {
      return false;
    }
  }

  private navigateOpenerWindow(targetUrl: string): void {
    try {
      window.opener.postMessage(
        { type: 'ai-chat-navigate', url: targetUrl },
        window.location.origin
      );
    } catch {
      window.open(window.location.origin + targetUrl, '_blank');
    }
  }

  private exportTableAsCsv(button: HTMLButtonElement): void {
    const tableWrapper = button.closest('.table-wrapper');
    const table = tableWrapper?.querySelector('table');

    if (!table) {
      return;
    }

    const rows = Array.from(table.querySelectorAll('tr'));
    const csvRows = rows
      .map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        return cells.map(cell => this.normalizeCsvCellValue(cell.textContent));
      })
      .filter(row => row.length > 0);

    if (!csvRows.length) {
      return;
    }

    downloadCsv(csvRows, `allo-table-${Date.now()}.csv`, {
      lineBreak: '\r\n',
      prependBom: true,
    });
  }

  private normalizeCsvCellValue(value: string | null): string {
    return (value ?? '').replace(/\s+/g, ' ').trim();
  }
}
